import type { ReactNode } from 'react';
import { PortableText } from '@portabletext/react';
import dayjs from 'dayjs';

import type { PublishedLineup } from '@api-service/predicted-lineups';
import {
  ARTICLE_TOKENS,
  SCROLL_MARGIN_CLASS
} from '@components/article/design-tokens';
import { buildArticlePortableTextComponents } from '@components/article/portable-text-components';
import { AvailabilityList } from '@components/predicted-lineups/AvailabilityList';
import {
  LineupPitch,
  LockedPitch
} from '@components/predicted-lineups/LineupPitch';
import { TeamBadge } from '@components/transfers/TeamBadge';
import type { ArticleBlockData } from '@lib/article-blocks/types';
import {
  buildClubAvailability,
  hasAnyAvailability,
  hiddenOptaIds
} from '@lib/predicted-lineups/availability';
import type { FlaggedPlayer } from '@lib/predicted-lineups/availability';
import { resolveLastUpdated } from '@lib/predicted-lineups/compose';
import type {
  ClubDescriptor,
  ClubHeading
} from '@lib/predicted-lineups/compose';
import type { PlayerFactsMap } from '@lib/predicted-lineups/player-facts';
import type { SanityPredictedLineupTeamSection } from '@lib/sanity';

const KICKOFF_FORMAT = 'ddd D MMM, HH:mm';
const UPDATED_FORMAT = 'ddd D MMM, HH:mm';

type ClubSectionProps = {
  club: ClubDescriptor;
  heading: ClubHeading;
  lineup?: PublishedLineup;
  playerFacts: PlayerFactsMap;
  /** FPL's injured, doubtful and suspended players, merged with the editor's overrides. */
  flaggedPlayers: FlaggedPlayer[];
  /** The club's words. Absent for a gated club the visitor cannot read. */
  words?: SanityPredictedLineupTeamSection;
  blockData: ArticleBlockData;
  /** Shown in place of the words when they are not available to this visitor. */
  wordsFallback?: ReactNode;
  /** Swaps the eleven for a blank pitch while the visitor cannot read this club. */
  isLocked?: boolean;
  /** Laid over the locked pitch and words. */
  lockedOverlay?: ReactNode;
};

/**
 * One club on the page: heading (which feeds the table of contents), the
 * fixture and freshness line, the pitch from the API, then the editor's words.
 */
export const ClubSection = ({
  club,
  heading,
  lineup,
  playerFacts,
  flaggedPlayers,
  words,
  blockData,
  wordsFallback,
  isLocked = false,
  lockedOverlay
}: ClubSectionProps) => {
  const lastUpdated = resolveLastUpdated(words, lineup);
  const clubName = club.teamName ?? club.teamShortName;
  const notes = words?.notes ?? [];
  const doubtOptaIds = new Set(
    (words?.doubts ?? []).map((entry) => entry.player.optaUuid)
  );
  const clearedOptaIds = hiddenOptaIds(words);
  // Only built once the visitor can read the club: for a gated club the FPL
  // flags are part of what the paywall sells.
  const availability = words && buildClubAvailability(words, flaggedPlayers);
  const hasAvailability = Boolean(
    availability && hasAnyAvailability(availability)
  );

  const lockedContent = (
    <>
      <LockedPitch />
      {wordsFallback}
    </>
  );
  const readableWords = words && (
    <>
      {notes.length > 0 && (
        <div className="content-body" data-cy="club-notes">
          <PortableText
            value={notes}
            components={buildArticlePortableTextComponents(blockData, [])}
          />
        </div>
      )}
      {availability && hasAvailability && (
        <div
          className={`mt-5 flex flex-col gap-3 rounded-[14px] border p-4 ${ARTICLE_TOKENS.rule} ${ARTICLE_TOKENS.surfaceTint}`}
          data-cy="club-availability"
        >
          <AvailabilityList
            title="Ruled out"
            entries={availability.ruledOut}
            playerFacts={playerFacts}
            tone="danger"
            testId="club-ruled-out"
          />
          <AvailabilityList
            title="Doubts"
            entries={availability.doubts}
            playerFacts={playerFacts}
            tone="warning"
            testId="club-doubts"
          />
          <AvailabilityList
            title="Suspended"
            entries={availability.suspensions}
            playerFacts={playerFacts}
            tone="neutral"
            testId="club-suspensions"
          />
        </div>
      )}
    </>
  );

  return (
    <section
      id={heading.id}
      className={`${SCROLL_MARGIN_CLASS} mt-14`}
      data-cy="club-section"
      data-team={club.teamShortName}
    >
      <h2
        className={`m-0 flex items-start gap-3 font-heading text-[22px] font-bold leading-[1.15] sm:text-[26px] ${ARTICLE_TOKENS.ink}`}
      >
        <TeamBadge
          shortName={club.teamShortName}
          size="lg"
          className="!mb-0 mt-px !h-7 !w-7 shrink-0 sm:!h-9 sm:!w-9"
        />
        <span>{heading.text}</span>
      </h2>
      <p
        className={`mb-0 mt-2 font-body text-[13px] ${ARTICLE_TOKENS.muted}`}
        data-cy="club-meta"
      >
        {club.kickoffAt && (
          <span>Kick-off {dayjs(club.kickoffAt).format(KICKOFF_FORMAT)}</span>
        )}
        {club.kickoffAt && lastUpdated && <span aria-hidden> · </span>}
        {lastUpdated && (
          <span>Last updated {dayjs(lastUpdated).format(UPDATED_FORMAT)}</span>
        )}
      </p>
      {isLocked ? (
        lockedOverlay ? (
          <div className="relative" data-cy="club-locked">
            {lockedContent}
            {lockedOverlay}
          </div>
        ) : (
          lockedContent
        )
      ) : (
        <>
          <LineupPitch
            lineup={lineup}
            playerFacts={playerFacts}
            clubName={clubName}
            doubtOptaIds={doubtOptaIds}
            clearedOptaIds={clearedOptaIds}
          />
          {words ? readableWords : wordsFallback}
        </>
      )}
    </section>
  );
};
